import { isDesktopMusicRhythmRuntime } from "../musicRhythm/desktopRuntime";

const CHANNEL_NAME = "eweadn_driver_active_tab";
const PAGE_ID_STORAGE_KEY = "driver_page_id";

/** 页签成为「当前操作页」时派发，供灯光轮询、布局等主动刷新 */
export const ACTIVE_DRIVER_TAB_CHANGED_EVENT = "eweadn:activeDriverTabChanged";

/** ?????? HID ?????????/???? refresh */
export const DRIVER_TAB_HARDWARE_RESYNC_EVENT =
  "eweadn:driverTabHardwareResynced";

/** 星闪等 SDK 从实体设备上推的事件名 */
const HARDWARE_PUSH_EVENTS = new Set([
  "switchConfig",
  "lightingBase",
  "customCommand",
  "usbChange",
  "lightingChange",
  "keyChange",
]);

let initialized = false;
let pageId = "";
let isPageVisible = true;
let globalActivePageId = "";
let channel: BroadcastChannel | null = null;
const listeners = new Set<() => void>();

/** 浏览器多页签场景；Electron 单窗口不走防串台 */
export function isBrowserMultiTabRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return !isDesktopMusicRhythmRuntime();
}

function ensurePageId(): string {
  if (pageId) return pageId;
  if (typeof sessionStorage !== "undefined") {
    const stored = sessionStorage.getItem(PAGE_ID_STORAGE_KEY);
    if (stored) {
      pageId = stored;
      return pageId;
    }
    pageId = `page_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(PAGE_ID_STORAGE_KEY, pageId);
    return pageId;
  }
  pageId = `page_${Math.random().toString(36).slice(2)}`;
  return pageId;
}

function notifyListeners(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ACTIVE_DRIVER_TAB_CHANGED_EVENT, {
        detail: {
          pageId: ensurePageId(),
          isActive: isActiveDriverTab(),
        },
      })
    );
  }
}

function claimActive(): void {
  const id = ensurePageId();
  globalActivePageId = id;
  try {
    channel?.postMessage({ type: "PAGE_ACTIVE", pageId: id, ts: Date.now() });
  } catch {
    /* ignore */
  }
}

/**
 * 浏览器多页签防串台：每个页签唯一 ID + BroadcastChannel 同步「当前操作页」。
 * 须在主应用启动时调用一次（commonApi 导入时会自动初始化）。
 */
export function initActiveDriverTab(): void {
  if (initialized || !isBrowserMultiTabRuntime()) return;
  initialized = true;

  const id = ensurePageId();
  globalActivePageId = id;
  isPageVisible =
    typeof document !== "undefined"
      ? document.visibilityState === "visible"
      : true;

  if (typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (ev: MessageEvent) => {
        const msg = ev?.data;
        if (msg?.type === "PAGE_ACTIVE" && typeof msg.pageId === "string") {
          globalActivePageId = msg.pageId;
          notifyListeners();
        }
      };
    } catch {
      /* ignore */
    }
  }

  document?.addEventListener("visibilitychange", () => {
    isPageVisible = document.visibilityState === "visible";
    if (isPageVisible) {
      claimActive();
    }
    notifyListeners();
  });

  window?.addEventListener("focus", () => {
    if (document.visibilityState === "visible") {
      claimActive();
      notifyListeners();
    }
  });

  if (isPageVisible) {
    claimActive();
  }
}

/** 当前页签是否为「有权处理实体设备上报 / 轮询回显」的操作页 */
export function isActiveDriverTab(): boolean {
  if (!isBrowserMultiTabRuntime()) return true;
  if (!initialized) {
    initActiveDriverTab();
  }
  if (!isPageVisible) return false;
  return globalActivePageId === ensurePageId();
}

export function getActiveDriverTabId(): string {
  return ensurePageId();
}

export function onActiveDriverTabChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 包装 SDK EventEmitter：仅当前激活页签执行硬件上推回调。
 * 对 getConfig 等主动调用无影响。
 */
export function wrapHardwareEventEmitter(emitter: unknown): void {
  if (!isBrowserMultiTabRuntime()) return;
  if (!emitter || typeof emitter !== "object") return;

  const target = emitter as {
    on?: (event: string, handler: (...args: unknown[]) => void) => void;
    off?: (event: string, handler: (...args: unknown[]) => void) => void;
    __activeTabWrapped?: boolean;
  };

  if (target.__activeTabWrapped || typeof target.on !== "function") return;
  target.__activeTabWrapped = true;

  const originalOn = target.on.bind(target);
  const originalOff =
    typeof target.off === "function" ? target.off.bind(target) : null;

  const handlerMap = new WeakMap<
    (...args: unknown[]) => void,
    (...args: unknown[]) => void
  >();

  const wrapHandler = (
    event: string,
    handler: (...args: unknown[]) => void
  ): ((...args: unknown[]) => void) => {
    if (!HARDWARE_PUSH_EVENTS.has(event)) {
      return handler;
    }
    const wrapped = (...args: unknown[]) => {
      if (!isActiveDriverTab()) return;
      return handler(...args);
    };
    handlerMap.set(handler, wrapped);
    return wrapped;
  };

  target.on = (event: string, handler: (...args: unknown[]) => void) => {
    return originalOn(event, wrapHandler(event, handler));
  };

  if (originalOff) {
    target.off = (event: string, handler: (...args: unknown[]) => void) => {
      const wrapped = handlerMap.get(handler) ?? handler;
      return originalOff(event, wrapped);
    };
  }
}
